import React, { FC, useEffect, useState } from 'react';
import { Route, RouteProps, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAuthState, getUser } from 'store/selectors/auth.selector';
import { getDealSourceData, getDealSourceDataFlags } from 'store/selectors/RACAffordabilityEngine.selector';
import { useIsRoleAllowed } from 'modules/core/hooks';
import { Loader } from 'modules/core/components';
import { ErrorPage } from 'pages/ErrorPage/ErrorPage';

import { NavigationService } from './navigation.service';
import { ROUTES_PERMISSIONS_MAP } from './const/common.const';
import { getFetchingLendersFlag, getLendersList } from '../store/selectors/lenders.selector';
import { getDisabledFeaturesList } from '../store/selectors/ui.selector';
import { LOGIN_URL_PREFIX, SSO_TYPE } from '../modules/core/constants';
import { getDealerSsoLoginModalLanding } from '../modules/auth/utils/dealerSsoLenderSelection.utils';
import { ROUTER_PATH } from './const/routerPath.const';
import { isAlternativeSsoLink, isDealSourceDataUpdated } from './utils/alternativeSso';

export interface IRouterItem extends RouteProps {
  isPrivate?: boolean;
  isAccessibleToAll?: boolean;
}

export const RouterItem: FC<IRouterItem> = ({
  component,
  render,
  path,
  exact,
  isPrivate = false,
  isAccessibleToAll,
}: IRouterItem) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const { isAuth, isFetching, isAlternativeSsoError } = useSelector(getAuthState);
  const lenders = useSelector(getLendersList);
  const isFetchingLendersList = useSelector(getFetchingLendersFlag);
  const disabledFeaturesList = useSelector(getDisabledFeaturesList);
  const isRoleAllowed = useIsRoleAllowed(ROUTES_PERMISSIONS_MAP.get(path as string));
  const user = useSelector(getUser);
  const dealSourceData = useSelector(getDealSourceData);
  const dealSourceDataFlags = useSelector(getDealSourceDataFlags);
  const isDealerSso = user?.is_sso === true && user?.sso_type === SSO_TYPE.DEALER;

  useEffect(() => {
    setIsLoading(isFetching);

    if (!isAuth && isPrivate) {
      NavigationService.navigateToLogin();
    }

    const isAlternativeSsoDataLoading =
      isAlternativeSsoLink() && !isDealSourceDataUpdated(dealSourceData) && !isAlternativeSsoError;

    if (
      isAuth &&
      user &&
      user.tenants &&
      window.location.pathname.includes('login') &&
      window.location.hostname.includes(LOGIN_URL_PREFIX) &&
      !isAlternativeSsoDataLoading
    ) {
      history.push(ROUTER_PATH.SELECT_DEALER);
    }

    if (
      isAuth &&
      user &&
      !isPrivate &&
      !isAccessibleToAll &&
      window.location.pathname.includes('login') &&
      (!user.tenants || !window.location.hostname.includes(LOGIN_URL_PREFIX))
    ) {
      setIsLoading(true);

      if (isDealerSso) {
        if (!isFetchingLendersList) {
          const { landingPath } = getDealerSsoLoginModalLanding(user, lenders, disabledFeaturesList);

          if (landingPath) {
            NavigationService.navigateToDashboard(
              user,
              disabledFeaturesList,
              lenders,
              null,
              landingPath,
              isAlternativeSsoError,
            );
          } else if (!isAlternativeSsoDataLoading && dealSourceDataFlags.isReceived) {
            NavigationService.navigateToDashboard(
              user,
              disabledFeaturesList,
              lenders,
              null,
              dealSourceData?.lender_path,
              isAlternativeSsoError,
            );
          }
        }
      } else if (!isAlternativeSsoDataLoading && (!user?.is_sso || dealSourceDataFlags.isReceived)) {
        NavigationService.navigateToDashboard(
          user,
          disabledFeaturesList,
          lenders,
          null,
          dealSourceData?.lender_path,
          isAlternativeSsoError,
        );
      }
    }
  }, [
    user,
    isAuth,
    isPrivate,
    isAccessibleToAll,
    isFetching,
    setIsLoading,
    disabledFeaturesList,
    lenders,
    history,
    dealSourceData,
    isAlternativeSsoError,
    dealSourceDataFlags,
    isDealerSso,
    isFetchingLendersList,
  ]);

  return isLoading ? (
    <Loader />
  ) : (
    <Route
      render={render}
      component={isRoleAllowed || !user?.isWithInventory ? component : ErrorPage}
      path={path}
      exact={exact}
    />
  );
};
